import React, { useCallback, useMemo, useState } from 'react';
import { VirtualizedList } from 'react-native';
import styled from 'styled-components/native';

import { ICountryDetail } from '@webtrader/core/api/account/country';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { getCountryWithCodeLabel, setCountryKeyString } from '@webtrader/core/format/country';
import { useTranslation } from '@webtrader/core/i18n';
import { useCountry } from '@webtrader/core/services/country';
import { useTheme } from '@webtrader/core/services/theme/v2';

import ActionSheetCheckmarkIcon from '@/components/ActionSheet/Checkmark';
import ActionSheetSelector from '@/components/ActionSheet/Selectors';
import ValidationError from '@/components/FormValidationError';
import FeatherIcons from '@/components/Icons/Feather';
import Modal from '@/components/Modal';
import TextInput from '@/components/TextInput';

import {
  StyledActionSheet,
  StyledDropdownSelectorContainer,
  StyledDropdownSelectorIcon,
  StyledDropdownSelectorText,
  StyledFormField,
  StyledFormTitle,
  StyledFormTitleContainer,
  StyledInputWrapper,
  StyledIonicons
} from '../styled';

interface ICountryDialCodeDropdownProps {
  value: string | undefined;
  onChange: ((value: string | undefined) => void) | undefined;
  showValidation: boolean;
  validationText: string;
}

const CountryDropdown: FCC<ICountryDialCodeDropdownProps> = (props) => {
  const { value, onChange, showValidation, validationText } = props;
  const { t } = useTranslation();
  const { theme } = useTheme();

  const [showOptions, setShowOptions] = useState(false);
  const [keyword, setKeyword] = useState('');

  const { countryDetails } = useCountry();

  const onOpen = useCallback(() => {
    setKeyword('');
    setShowOptions(true);
  }, []);

  const onClose = useCallback(() => {
    setKeyword('');
    setShowOptions(false);
  }, []);

  const onSelectCountry = useCallback(
    (countryCode: string) => {
      onChange?.(countryCode);

      onClose();
      setKeyword('');
    },
    [onChange, onClose]
  );

  const selectedCountry = countryDetails.find((c) => c.countryCode === value);

  const countryOptions = useMemo(() => {
    return countryDetails.filter(({ country }) =>
      country.replace(' ', '').toLowerCase().includes(keyword.replace(' ', '').toLowerCase())
    );
  }, [keyword, countryDetails]);

  return (
    <StyledFormField isPaddingBottom>
      <StyledFormTitleContainer>
        <StyledIonicons name="earth" size={24} />
        <StyledFormTitle>{t('Country of Residence')}</StyledFormTitle>
      </StyledFormTitleContainer>
      <StyledInputWrapper>
        <StyledActionSheet onPress={onOpen}>
          <ActionSheetSelector
            label={getCountryWithCodeLabel(selectedCountry)}
            withBorder={true}
            testID={EDataTestId.DEMO_ACCOUNT_CREATION_MODAL_DEPOSIT}
          />
        </StyledActionSheet>
        <Modal title={t('Country of Residence')} visible={showOptions} onClose={onClose}>
          <ModalContentSearch>
            <TextInput
              value={keyword}
              prefix={<FeatherIcons name="search" size={18} color={theme.colors.text[2]} />}
              placeholder="Search"
              onChangeText={setKeyword}
              testID={EDataTestId.COUNTRY_DIAL_CODE_SEARCH}
            />
          </ModalContentSearch>
          <VirtualizedList<ICountryDetail>
            showsVerticalScrollIndicator={false}
            data={countryOptions}
            getItem={(data, i) => data[i]}
            getItemCount={() => countryOptions.length}
            keyExtractor={(item) => setCountryKeyString(item)}
            initialNumToRender={30}
            renderItem={({ item }) => {
              const selected = value === item.countryCode;

              return (
                <StyledDropdownSelectorContainer
                  selected={selected}
                  activeOpacity={1}
                  onPress={() => onSelectCountry(item.countryCode)}
                  testID={EDataTestId.COUNTRY_DIAL_CODE}
                >
                  <StyledDropdownSelectorText>{getCountryWithCodeLabel(item)}</StyledDropdownSelectorText>
                  <StyledDropdownSelectorIcon>{selected && <ActionSheetCheckmarkIcon />}</StyledDropdownSelectorIcon>
                </StyledDropdownSelectorContainer>
              );
            }}
          />
        </Modal>
        <ValidationError showValidation={showValidation} message={validationText} />
      </StyledInputWrapper>
    </StyledFormField>
  );
};

export default CountryDropdown;

const ModalContentSearch = styled.View`
  padding: ${dynamicSize.convertSize(10)}px;
`;
